import type { HandoffLatency } from "../../../api/work";
import { formatQueueHours } from "./pipelineHelpers";

interface Props {
  /** The cells' slots, from the same `layoutCells` call the cells use. */
  cellLayout: Array<{ x: number; width: number }>;
  /** Entry `i` is the hand-off between cell `i` and cell `i + 1`. */
  handoffs: Array<HandoffLatency | null>;
  /** Text baseline. */
  y: number;
  laneColor: string;
}

const MUTED = "#555970";

/**
 * How long work queues before each move to the next state, drawn on the
 * gap between the two cells.
 *
 * The route has always computed `handoff_latencies` for every adjacent
 * pair; the lane card showed only the worst one. The predecessor's
 * `HandoffMarker` was written for this and never wired. The lane's
 * slowest hand-off is drawn in the lane colour, and a pair with no
 * hand-off in the window reads "—" rather than "0h": the route returns
 * `null` there precisely so that "never happens" and "instant" stay apart.
 */
export function HandoffStrip({ cellLayout, handoffs, y, laneColor }: Props) {
  const measured = handoffs.flatMap((h) =>
    h && h.median_hours !== null ? [h.median_hours] : [],
  );
  // Only worth singling out when there is something to compare it with.
  const worst = measured.length > 1 ? Math.max(...measured) : null;

  return (
    <g>
      {handoffs.map((h, i) => {
        const left = cellLayout[i];
        const right = cellLayout[i + 1];
        if (!h || !left || !right) return null;
        const cx = (left.x + left.width + right.x) / 2;
        const hours = h.median_hours;
        const isWorst = hours !== null && hours === worst;
        const label = hours === null ? "—" : formatQueueHours(hours);
        const title =
          hours === null
            ? `${h.from_state} → ${h.to_state}: no hand-off in the window`
            : `${h.from_state} → ${h.to_state}: median ${formatQueueHours(hours)} ` +
              `in ${h.from_state} before the move ` +
              `(${h.samples} hand-off${h.samples === 1 ? "" : "s"})`;
        return (
          <g key={`${h.from_state}→${h.to_state}`}>
            <title>{title}</title>
            <text
              x={cx}
              y={y}
              textAnchor="middle"
              fontFamily="Inter, -apple-system, sans-serif"
              fontSize={6}
              fontWeight={isWorst ? 700 : 500}
              fill={isWorst ? laneColor : MUTED}
            >
              {`→ ${label}`}
            </text>
          </g>
        );
      })}
    </g>
  );
}
